import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../../Styles/PageStyles";
import Images from "../../../Images";
import tw from "tailwind-styled-components";
import { PaginationLimit } from "../../../Config";
import {
    capitalizeFirstLetter,
    getCategoryIcon,
    getFoodCategoryLabel,
    getFoodUnitLabel,
    getStockStatus,
    handleCopy,
    truncateText
} from "../../../helpers";
import moment from "moment";
import { IoCopySharp } from "react-icons/io5";
import { MdEdit, MdDelete } from "react-icons/md";
import Tooltip from "../../Tooltip";
import { getDateTimeDisplay } from "../../../utilities/helpers";

const Table = ({
    apiData,
    selectedData,
    setSelectedData,
    setIsShowEditItem,
    setIsDeleteItem
}) => {
    const [data, setData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [currentPage, setCurrentPage] = useState(0);

    const editItem = (item) => {
        setSelectedData(item);
        setIsShowEditItem(true);
    };

    const deleteItem = (item) => {
        setSelectedData(item);
        setIsDeleteItem(true);
    }

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    useEffect(() => {
        if (apiData && apiData.length > 0) {
            const tempData = apiData.map((item) => ({
                id: item.id,
                name: item.name,
                categoryId: item.categoryId,
                //unitType: item.unitType,
                //status: item.status,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
                // For display
                itemName: getItemName(item),
                category: getCategoryName(item),
                //statusBadge: getStatus(item),
                updatedAtDisplay: getDateTimeDisplay (item.updatedAt),
                actions: getActions(item),
            }));
            setData(tempData);
        } else {
            setData([]);
        }
    }, [apiData]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getItemName = (item) => {
        const unitLabel = getFoodUnitLabel(item.unitType);
        return (
            <UserImageWrapper>
                <IconWrapper>
                    {getCategoryIcon(item.categoryId)}
                </IconWrapper>
                <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2 group">
                        <span className="font-medium text-gray-700 truncate">
                            {capitalizeFirstLetter(truncateText(item.name, 28))}
                        </span>
                    </div>
                    <span className="text-gray-400 text-xs">{unitLabel}</span>
                </div>
            </UserImageWrapper>
        );
    };

    const getCategoryName = (item) => {
        const label = getFoodCategoryLabel(item.categoryId);
        return (
            <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-600">
                    {label}
                </span>
            </div>
        );
    };

    const getActions = (item) => {
        return (
            <div className="flex items-center gap-3">
                <Tooltip label="Click to edit this item">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            editItem(item);
                        }}
                        className="text-gray-400 hover:text-primary-color transition"
                    // title="Edit Item"
                    >
                        <MdEdit size={18} />
                    </button>
                </Tooltip>
                <Tooltip label="Click to delete this item">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            deleteItem(item);
                        }}
                        className="p-2 rounded-full hover:bg-rose-100 transition cursor-pointer"
                    // title="Delete Item"
                    >
                        <MdDelete size={18} className="text-gray-400 hover:text-rose-500" />
                    </button>
                </Tooltip>
            </div>
        );
    };

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Item",
                    accessor: "itemName",
                },
                {
                    Header: "Category",
                    accessor: "category",
                },

                {
                    Header: "Actions",
                    accessor: "actions",
                },
            ];
        }
        return [
            {
                Header: "Item",
                accessor: "itemName",
                sortType: (a, b) => a.original.name.localeCompare(b.original.name),
            },

            {
                Header: "Category",
                accessor: "category",
            },


            {
                Header: "Date",
                accessor: "updatedAtDisplay",
            },
            {
                Header: "Actions",
                accessor: "actions",
                disableSortBy: true,
            },
        ];
    }, [isMobileView,]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        setCurrentPage(pageIndex);
    }, [pageIndex]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(data?.length > 0).toString()}>
                    {page.length > 0 && (
                        page.map((row, rowIndex) => {
                            prepareRow(row);
                            return (
                                <React.Fragment key={rowIndex}>
                                    <Tr {...row.getRowProps({ style: { height: "80px" } })} hasdata={(data?.length > 0).toString()}>
                                        {row.cells.map((cell, cellIndex) => {
                                            // Don't make action cells clickable for expansion
                                            const isActionCell = cell.column.id === 'actions' ||
                                                (typeof cell.column.accessor === 'string' &&
                                                    cell.column.accessor === 'actions');
                                            return (
                                                <Td
                                                    {...cell.getCellProps()}
                                                    onClick={() => {
                                                        if (!isActionCell &&
                                                            (cellIndex === 0 ||
                                                                (cellIndex === 1 && window.innerWidth < 768))) {
                                                            toggleRow(rowIndex);
                                                        }
                                                    }}
                                                >
                                                    {cell.render("Cell")}
                                                </Td>
                                            );
                                        })}
                                    </Tr>
                                    {selectedRow === rowIndex && window.innerWidth < 768 && (
                                        <Tr>
                                            <Td colSpan={columns.length}>
                                                <ul className="flex flex-col gap-2 p-2">
                                                    <li>
                                                        <strong>Category:</strong>{" "}
                                                        {getFoodCategoryLabel(data[rowIndex]?.categoryId)}
                                                    </li>
                                                    <li>
                                                        <strong>Unit Type:</strong>{" "}
                                                        {getFoodUnitLabel(data[rowIndex]?.unitType)}
                                                    </li>

                                                    <li>
                                                        <strong>Created At:</strong>{" "}
                                                        {moment(data[rowIndex]?.createdAt).format("DD, MMM YY hh:mm A")}
                                                    </li>
                                                    <li>
                                                        <strong>Last Updated:</strong>{" "}
                                                        {data[rowIndex]?.updatedAtDisplay}
                                                    </li>
                                                    <li>
                                                        <strong>Actions:</strong>{" "}
                                                        {data[rowIndex]?.actions}
                                                    </li>
                                                </ul>
                                            </Td>
                                        </Tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </Tbody>
            </CustomTable>
            {data?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>
                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75" />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const UserImageWrapper = tw.div`flex items-center gap-2`;
const IconWrapper = tw.div`w-8 h-8 flex items-center justify-center rounded-full bg-primary-color/10`;
const CustomTable = tw.table`w-full text-zinc-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-1 text-sm md:p-2 text-left`;
const ActionButton = tw.button`
    p-1 rounded-full hover:bg-gray-100 transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-1
`;

export default Table;